import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MOVE_TYPES, type LoadPatchPayload, type LoadRow } from "@/lib/api";
import { formatMoveType } from "@/lib/format";

type FieldKind = "text" | "numeric" | "moveType" | "date";

interface FieldSpec {
  name: string;
  label: string;
  kind: FieldKind;
}

// The parsed ticket fields the driver sees on review/detail. Names match
// the server's EDITABLE_FIELDS allowlist exactly — anything else would be
// rejected with a 400.
export const LOAD_FIELDS: FieldSpec[] = [
  { name: "containerNo", label: "Container #", kind: "text" },
  { name: "moveType", label: "Move type", kind: "moveType" },
  { name: "carrierName", label: "Carrier", kind: "text" },
  { name: "carrierCode", label: "Carrier code", kind: "text" },
  { name: "grossWeightLb", label: "Gross weight (lb)", kind: "numeric" },
  { name: "eirDatetime", label: "Ticket date", kind: "date" },
];

const KIND_BY_NAME = new Map(LOAD_FIELDS.map((f) => [f.name, f.kind] as const));

// ISO timestamp -> "YYYY-MM-DD" for <input type="date">, in the viewer's
// local time. Empty string if absent/invalid.
function toDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Build the editable form state from a load, OMITTING fields the parser
// came back null on. A blank input beside a label reads as "we lost your
// data"; leaving the field out entirely reads as "the ticket didn't say",
// which is the truth. The driver can still fix anything that IS there.
export function buildFieldState(load: LoadRow): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of LOAD_FIELDS) {
    const raw = load[f.name as keyof LoadRow];
    if (raw === null || raw === undefined || raw === "") continue;
    out[f.name] = f.kind === "date" ? toDateInput(String(raw)) : String(raw);
  }
  return out;
}

// Diff the form against its baseline and coerce each changed field to the
// representation the PATCH endpoint expects. Unchanged fields are absent
// from the payload, so a save never rewrites provenance on a field the
// driver didn't touch.
export function collectFieldPatch(
  values: Record<string, string>,
  baseline: Record<string, string>,
): LoadPatchPayload {
  const patch: LoadPatchPayload = {};

  for (const name of Object.keys(values)) {
    const next = values[name] ?? "";
    if (next === (baseline[name] ?? "")) continue;

    const kind = KIND_BY_NAME.get(name);
    if (kind === undefined) continue;

    if (next.trim() === "") {
      patch[name] = null;
      continue;
    }
    if (kind === "numeric") {
      patch[name] = Number(next);
    } else if (kind === "date") {
      // The ticket date has no time of day; send midnight local as ISO
      // and let the server store it.
      const d = new Date(`${next}T00:00:00`);
      patch[name] = Number.isNaN(d.getTime()) ? next : d.toISOString();
    } else {
      patch[name] = next.trim();
    }
  }

  return patch;
}

interface LoadFieldsProps {
  load: LoadRow;
  values: Record<string, string>;
  baseline: Record<string, string>;
  onChange: (name: string, value: string) => void;
}

export function LoadFields({ load, values, baseline, onChange }: LoadFieldsProps) {
  // Only render the fields the parser actually produced (see
  // buildFieldState). If it produced none, say so plainly.
  const present = LOAD_FIELDS.filter((f) => f.name in values);

  if (present.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Couldn&apos;t read any details off this ticket. You can still save the amount.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {present.map((f) => {
        const value = values[f.name] ?? "";
        const dirty = value !== (baseline[f.name] ?? "");
        return (
          <div key={f.name} className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor={f.name}>{f.label}</Label>
              {dirty && <span className="text-xs font-medium text-primary">edited</span>}
            </div>
            {f.kind === "moveType" ? (
              <select
                id={f.name}
                aria-label={f.label}
                value={value}
                onChange={(e) => onChange(f.name, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">—</option>
                {MOVE_TYPES.map((m) => (
                  <option key={m} value={m}>
                    {formatMoveType(m)}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id={f.name}
                type={f.kind === "numeric" ? "number" : f.kind === "date" ? "date" : "text"}
                inputMode={f.kind === "numeric" ? "decimal" : undefined}
                value={value}
                onChange={(e) => onChange(f.name, e.target.value)}
              />
            )}
          </div>
        );
      })}

      {load.parserConfidence != null && (
        <p className="pt-2 text-xs text-muted-foreground border-t">
          Ticket read with {Math.round(load.parserConfidence * 100)}% confidence.
        </p>
      )}
    </div>
  );
}
